/**
 * CubingModal.jsx - Cubing (potential reroll) UI
 * MapleStory-style equipment potential system with animation and effects
 *
 * CSS Prefix: cub- (Cubing)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { CUBE_TYPES, CUBING_CONFIG, getGradeInfo, formatPotentialLine, getNextGrade } from '../../data/cubing';
import { getGradeUpInfo, getPotentialDisplayInfo } from '../../modules/cubing';
import ItemChip from '../UI/ItemChip';
import './CubingModal.css';

function CubingModal({ item, onClose }) {
  const { playerCharacter, applyCube, getCubeCount } = useGame();

  const [selectedCube, setSelectedCube] = useState(null);
  const [isCubing, setIsCubing] = useState(false);
  const [cubeResult, setCubeResult] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(null); // 'spinning', 'reveal', 'result'
  const [showKeepChoice, setShowKeepChoice] = useState(false);

  // Track current potential state (updates after cubing)
  const [currentGrade, setCurrentGrade] = useState(item?.potentialGrade || null);
  const [currentLines, setCurrentLines] = useState(item?.potentialLines || []);
  const [pendingLines, setPendingLines] = useState(null); // New lines from Black Cube before choice

  // Sync state from prop on initial load or if item changes
  useEffect(() => {
    setCurrentGrade(item?.potentialGrade || null);
    setCurrentLines(item?.potentialLines || []);
    setCubeResult(null);
    setPendingLines(null);
    setShowKeepChoice(false);
  }, [item?.instanceId]);

  // Get cube counts
  const cubeCounts = useMemo(() => {
    const counts = {};
    Object.keys(CUBE_TYPES).forEach(cubeId => {
      counts[cubeId] = getCubeCount(cubeId);
    });
    return counts;
  }, [getCubeCount, playerCharacter?.inventory]);

  // Get grade-up info for selected cube
  const gradeUpInfo = useMemo(() => {
    if (!selectedCube) return null;
    return getGradeUpInfo(selectedCube, currentGrade);
  }, [selectedCube, currentGrade]);

  // Check if can apply selected cube
  const canApply = useMemo(() => {
    if (!selectedCube) return false;
    const cubeCount = cubeCounts[selectedCube] || 0;
    return cubeCount > 0;
  }, [selectedCube, cubeCounts]);

  // Handle cube application
  const handleApplyCube = useCallback(() => {
    if (!canApply || isCubing) return;

    const cube = CUBE_TYPES[selectedCube];

    setIsCubing(true);
    setCubeResult(null);
    setShowKeepChoice(false);
    setAnimationPhase('spinning');

    // Spinning phase (1.2s)
    setTimeout(() => {
      setAnimationPhase('reveal');

      // Reveal phase (0.5s)
      setTimeout(() => {
        // Perform cube application
        const result = applyCube(item.instanceId, selectedCube);
        setCubeResult(result);
        setAnimationPhase('result');

        if (result.success) {
          // Black Cube: Show choice to keep current or accept new
          if (cube.canKeepPrevious && result.newLines) {
            setPendingLines(result.newLines);
            setShowKeepChoice(true);
          } else {
            // Apply new lines immediately
            setCurrentGrade(result.newGrade);
            setCurrentLines(result.newLines);
          }
        }

        // Allow retry after seeing result
        setTimeout(() => {
          setIsCubing(false);
          setAnimationPhase(null);
        }, 500);
      }, 500);
    }, 1200);
  }, [canApply, isCubing, selectedCube, item.instanceId, applyCube]);

  // Handle Black Cube choice
  const handleKeepChoice = useCallback((keepCurrent) => {
    if (keepCurrent) {
      // Keep current lines, discard pending
      setPendingLines(null);
    } else {
      // Accept new lines
      if (cubeResult) {
        setCurrentGrade(cubeResult.newGrade);
        setCurrentLines(cubeResult.newLines);
      }
      setPendingLines(null);
    }
    setShowKeepChoice(false);
  }, [cubeResult]);

  // Current grade display info
  const currentGradeInfo = currentGrade ? getGradeInfo(currentGrade) : null;
  const nextGrade = getNextGrade(currentGrade);
  const nextGradeInfo = nextGrade ? getGradeInfo(nextGrade) : null;

  // Get result message
  const getResultMessage = () => {
    if (!cubeResult) return null;

    if (cubeResult.gradeUp) {
      const prevInfo = getGradeInfo(cubeResult.previousGrade);
      const newInfo = getGradeInfo(cubeResult.newGrade);
      return (
        <div className="cub-result grade-up">
          <span className="cub-result-icon">🎉</span>
          <span className="cub-result-text">
            Grade Up! {prevInfo?.shortCode || '--'} → {newInfo?.shortCode}
          </span>
        </div>
      );
    } else if (cubeResult.success) {
      return (
        <div className="cub-result reroll">
          <span className="cub-result-icon">🎲</span>
          <span className="cub-result-text">Lines Rerolled!</span>
        </div>
      );
    }
    return null;
  };

  if (!item) return null;

  return (
    <div className="cub-overlay" onClick={onClose}>
      <div className="cub-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cub-header">
          <span className="cub-header-icon">🎲</span>
          <span className="cub-header-title">Cube Equipment</span>
          <button className="cub-close" onClick={onClose}>×</button>
        </div>

        {/* Item Preview */}
        <div className={`cub-item-preview ${animationPhase || ''}`}>
          <div className="cub-item-container">
            <ItemChip item={{ ...item, potentialGrade: currentGrade, potentialLines: currentLines }} size="large" showName={false} />
          </div>
          <div className="cub-item-name">{item.name}</div>

          {/* Current Grade Badge */}
          <div className="cub-grade-badge" style={{ '--grade-color': currentGradeInfo?.color || '#6b7280' }}>
            <span className="cub-grade-icon">{currentGradeInfo?.icon || '⚪'}</span>
            <span className="cub-grade-name">{currentGradeInfo?.name || 'No Potential'}</span>
          </div>
        </div>

        {/* Current Potential Lines */}
        <div className="cub-lines-section">
          <div className="cub-lines-header">
            {showKeepChoice ? 'Choose Potential:' : 'Current Potential:'}
          </div>

          {showKeepChoice && pendingLines ? (
            <div className="cub-choice-container">
              {/* Current Lines (left) */}
              <div className="cub-choice-option current" onClick={() => handleKeepChoice(true)}>
                <div className="cub-choice-label">Keep Current</div>
                <div className="cub-lines-list">
                  {currentLines.length > 0 ? (
                    currentLines.map((line, i) => (
                      <div key={i} className="cub-line" style={{ '--line-color': currentGradeInfo?.color }}>
                        {formatPotentialLine(line)}
                      </div>
                    ))
                  ) : (
                    <div className="cub-no-lines">No lines</div>
                  )}
                </div>
              </div>

              {/* New Lines (right) */}
              <div className="cub-choice-option new" onClick={() => handleKeepChoice(false)}>
                <div className="cub-choice-label">Accept New</div>
                <div className="cub-lines-list">
                  {pendingLines.map((line, i) => (
                    <div key={i} className="cub-line new" style={{ '--line-color': cubeResult?.newGrade ? getGradeInfo(cubeResult.newGrade)?.color : currentGradeInfo?.color }}>
                      {formatPotentialLine(line)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="cub-lines-list">
              {currentLines.length > 0 ? (
                currentLines.map((line, i) => (
                  <div key={i} className="cub-line" style={{ '--line-color': currentGradeInfo?.color }}>
                    {formatPotentialLine(line)}
                  </div>
                ))
              ) : (
                <div className="cub-no-lines">No potential - Use a cube to reveal!</div>
              )}
            </div>
          )}
        </div>

        {/* Cube Selection */}
        <div className="cub-cubes-section">
          <div className="cub-cubes-header">Select Cube:</div>
          <div className="cub-cubes-grid">
            {Object.entries(CUBE_TYPES).map(([cubeId, cube]) => {
              const count = cubeCounts[cubeId] || 0;
              const isSelected = selectedCube === cubeId;
              return (
                <button
                  key={cubeId}
                  className={`cub-cube-btn ${isSelected ? 'selected' : ''} ${count === 0 ? 'empty' : ''}`}
                  onClick={() => count > 0 && setSelectedCube(cubeId)}
                  disabled={count === 0 || isCubing}
                  title={cube.description}
                >
                  <span className="cub-cube-icon">{cube.icon}</span>
                  <span className="cub-cube-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Grade-Up Info */}
        {selectedCube && gradeUpInfo && (
          <div className="cub-gradeup-info">
            {gradeUpInfo.canUpgrade ? (
              <>
                <span className="cub-gradeup-label">Grade-Up Chance:</span>
                <span className="cub-gradeup-value" style={{ color: nextGradeInfo?.color }}>
                  {Math.round(gradeUpInfo.chance * 100)}% → {nextGradeInfo?.shortCode}
                </span>
              </>
            ) : (
              <span className="cub-gradeup-max">
                {currentGrade === 'legendary' ? '🌟 Maximum Grade!' : 'No grade-up with this cube'}
              </span>
            )}
          </div>
        )}

        {/* Result Message */}
        {!showKeepChoice && getResultMessage()}

        {/* Apply Button */}
        {!showKeepChoice && (
          <div className="cub-actions">
            <button
              className={`cub-apply-btn ${canApply ? '' : 'disabled'} ${isCubing ? 'cubing' : ''}`}
              onClick={handleApplyCube}
              disabled={!canApply || isCubing}
            >
              <span className="cub-apply-icon">🎲</span>
              {isCubing ? 'Cubing...' : selectedCube ? `Use ${CUBE_TYPES[selectedCube].name}` : 'Select a Cube'}
              <span className="cub-apply-icon">🎲</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CubingModal;
